import React, { useRef, useState, useEffect } from "react";
import { Navigation, Clock, Car, MousePointer, Search, LocateFixed, MapPinned, Plus, X, RotateCcw, Fuel, RefreshCw, Tent, Eye, Droplet, Coffee, Trees, Caravan, PinOff, ExternalLink, Map, Maximize2, Minimize2, Bike, ParkingCircle, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { FUEL_TYPES, formatCurrency } from "./constants";

const POI_ICONS = {
  camp_site: Tent, campsite: Tent, viewpoint: Eye, scenic: Eye, fuel: Fuel, toilets: Droplet, rest_area: Coffee, hiking: Trees,
  footway: Trees, path: Trees, track: Trees, protected_area: Trees,
  nzmca: Caravan, caravan_site: Caravan, caravan: Caravan,
  cycling: Bike, water: Droplet, dump_station: Trash2, free_parking: ParkingCircle,
  towing_restriction: AlertTriangle,
};


// Build Google Maps directions URL
const buildGoogleMapsUrl = (origin, destination, waypoints = []) => {
  const params = new URLSearchParams();
  params.set("api", "1");
  params.set("origin", `${origin.lat},${origin.lng}`);
  params.set("destination", `${destination.lat},${destination.lng}`);
  params.set("travelmode", "driving");
  if (waypoints.length > 0) {
    params.set("waypoints", waypoints.join("|"));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

// Build Apple Maps directions URL (maps: protocol opens native Apple Maps)
const buildAppleMapsUrl = (origin, destination, originName, destName) => {
  return `maps://?daddr=${destination.lat},${destination.lng}&dirflg=d`;
};

const SuggestionItem = ({ s, onClick }) => {
  const isPoi = s.source === "poi";
  const IconComp = isPoi ? (POI_ICONS[s.poi_type] || MapPinned) : Search;
  return (
    <button className="sf-suggestion-item" onMouseDown={(e) => { e.preventDefault(); onClick(s); }}>
      <IconComp className={`w-3 h-3 flex-shrink-0 ${isPoi ? 'text-[#16A34A]' : 'text-[#E52020]'}`} />
      <span className="flex-1 text-left">{s.display_name.split(",").slice(0, 3).join(",")}</span>
      {isPoi && s.poi_type && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[rgba(22,163,74,0.1)] text-[#16A34A] font-['Manrope'] flex-shrink-0">
          {s.poi_type.replace(/_/g, " ")}
        </span>
      )}
    </button>
  );
};

export default function MapWidget({
  mapContainerRef,
  selectingMode, setSelectingMode,
  originInput, handleOriginSearch,
  originSuggestions, showOriginSuggestions, setShowOriginSuggestions,
  selectOriginSuggestion, locateMe,
  destinationInput, handleDestSearch,
  destSuggestions, showDestSuggestions, setShowDestSuggestions,
  selectDestSuggestion,
  waypoints, waypointInput, setWaypointInput,
  showWaypointInput, setShowWaypointInput,
  waypointInputRef, addWaypoint, removeWaypoint,
  calculateRoute, clearRoute, loadingRoute,
  routeInfo, altRouteInfo, switchRoute,
  user, setShowLogin, setCommuteFromRoute,
  consumption, selectedFuelType, returnTrip,
  poiEnabled, setPoiEnabled, poiTypes, setPoiTypes,
  addingCustomPin, onStartCustomPin, onCancelCustomPin,
  originCoords, destinationCoords, fuelStations,
  routeStations, loadingStations,
}) {
  const POI_OPTIONS = [
    { key: "campsite", label: "Campsites", Icon: Tent, color: "#16A34A" },
    { key: "nzmca", label: "NZMCA", Icon: Caravan, color: "#E85D04" },
    { key: "caravan", label: "Caravan", Icon: Caravan, color: "#D97706" },
    { key: "viewpoint", label: "Lookouts", Icon: Eye, color: "#8B5CF6" },
    { key: "scenic", label: "Scenic", Icon: Eye, color: "#8B5CF6" },
    { key: "fuel", label: "Fuel", Icon: Fuel, color: "#F59E0B" },
    { key: "toilets", label: "Toilets", Icon: Droplet, color: "#666666" },
    { key: "rest_area", label: "Rest Stops", Icon: Coffee, color: "#E52020" },
    { key: "hiking", label: "Walks", Icon: Trees, color: "#16A34A" },
    { key: "cycling", label: "Cycling", Icon: Bike, color: "#E52020" },
    { key: "water", label: "Water", Icon: Droplet, color: "#3B82F6" },
    { key: "dump_station", label: "Dump", Icon: Trash2, color: "#92400E" },
    { key: "free_parking", label: "Parking", Icon: ParkingCircle, color: "#475569" },
  ];
  const togglePoiType = (key) => {
    setPoiTypes(prev => prev.includes(key) ? prev.filter(t => t !== key) : [...prev, key]);
  };
  const [mapFullscreen, setMapFullscreen] = useState(false);

  // CSS-based fullscreen: body class kills ancestor animations, position:fixed covers viewport
  useEffect(() => {
    if (mapFullscreen) {
      document.body.classList.add('map-fullscreen-active');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('map-fullscreen-active');
      document.body.style.overflow = '';
    }
    // Resize mapbox after layout change - use window resize event as Mapbox listens to it
    const doResize = () => {
      window.dispatchEvent(new Event('resize'));
      if (window._mapboxMap) window._mapboxMap.resize();
    };
    requestAnimationFrame(() => {
      doResize();
      setTimeout(doResize, 150);
      setTimeout(doResize, 400);
    });
    return () => {
      document.body.classList.remove('map-fullscreen-active');
      document.body.style.overflow = '';
    };
  }, [mapFullscreen]);

  // Escape key to exit fullscreen
  useEffect(() => {
    if (!mapFullscreen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setMapFullscreen(false); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [mapFullscreen]);

  return (
    <div
      className="sf-map-widget"
      style={mapFullscreen ? { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999, borderRadius: 0, border: 'none', background: '#0A0F1A' } : {}}
      data-testid="map-widget-container"
    >
      <div ref={mapContainerRef} className={mapFullscreen ? 'map-container-fullscreen' : 'map-container'} data-testid="mapbox-map" />

      {/* Fullscreen toggle button */}
      <button
        onClick={() => setMapFullscreen(prev => !prev)}
        className={mapFullscreen
          ? 'fixed bottom-6 left-1/2 -translate-x-1/2 z-[100000] bg-white border border-[#EBE6DC] rounded-full shadow-lg px-5 py-3 hover:bg-[#F7F5F0] transition-colors flex items-center gap-2'
          : 'absolute top-3 right-14 z-[100000] bg-white border border-[#EBE6DC] rounded-lg shadow-lg p-2.5 hover:bg-[#F7F5F0] transition-colors'}
        data-testid="map-fullscreen-toggle"
      >
        {mapFullscreen ? (
          <><Minimize2 className="w-5 h-5 text-[#1A1A1A]" /><span className="text-xs font-['Manrope'] font-bold text-[#1A1A1A] tracking-wide">CLOSE MAP</span></>
        ) : (
          <Maximize2 className="w-4 h-4 text-[#1A1A1A]" />
        )}
      </button>

      {/* Route info in fullscreen */}
      {mapFullscreen && routeInfo && (
        <div className="absolute top-3 left-3 z-[99999] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 flex-wrap border border-[#EBE6DC]" style={{ maxWidth: 'calc(100% - 64px)' }}>
          <div className="flex items-center gap-1.5"><Navigation className="w-4 h-4 text-[#16A34A]" /><span className="font-mono font-bold text-[#1A1A1A] text-sm">{routeInfo.distance} km</span></div>
          <div className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#E52020]" /><span className="font-mono text-[#1A1A1A] text-sm">{routeInfo.duration}</span></div>
          {originCoords && destinationCoords && (
            <>
              <a href={buildGoogleMapsUrl(originCoords, destinationCoords, waypoints)} target="_blank" rel="noopener"
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-['Manrope'] font-bold text-[#4285F4] bg-[#4285F4]/5 border border-[#4285F4]/20 rounded-sm">
                <Map className="w-3 h-3" />Google
              </a>
              <a href={buildAppleMapsUrl(originCoords, destinationCoords, originInput, destinationInput)} target="_blank" rel="noopener"
                className="flex items-center gap-1 px-2 py-1 text-[10px] font-['Manrope'] font-bold text-[#1A1A1A] bg-[#1A1A1A]/5 border border-[#1A1A1A]/15 rounded-sm">
                <Map className="w-3 h-3" />Apple
              </a>
            </>
          )}
        </div>
      )}

      {/* Floating Search Overlay */}
      <div className={mapFullscreen ? "sf-map-overlay sf-map-overlay-fullscreen" : "sf-map-overlay"} onClick={(e) => e.stopPropagation()}>
        <div className="sf-map-mode">
          <MousePointer className="w-3 h-3" />
          <span>{selectingMode === "origin" ? "SETTING START" : "SETTING DESTINATION"}</span>
        </div>

        {/* Origin */}
        <div className="sf-search-row" onClick={() => setSelectingMode("origin")}>
          <div className="sf-dot-origin" />
          <div className="sf-search-ac" data-testid="origin-input" style={{ position: "relative", flex: 1 }}>
            <input type="text" className="sf-search-input" placeholder="From: type or click map" value={originInput}
              onChange={(e) => handleOriginSearch(e.target.value)}
              onFocus={() => { setSelectingMode("origin"); if (originSuggestions.length > 0) setShowOriginSuggestions(true); }}
              onKeyDown={(e) => { if (e.key === "Enter") setShowOriginSuggestions(false); }}
              data-testid="origin-text-input"
            />
            {showOriginSuggestions && originSuggestions.length > 0 && (
              <div className="sf-suggestions" data-testid="origin-suggestions">
                {originSuggestions.map((s, i) => (
                  <SuggestionItem key={s.place_id || s.display_name || i} s={s} onClick={selectOriginSuggestion} />
                ))}
              </div>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); locateMe(); }} className="sf-locate-btn" title="Use my current location" data-testid="use-my-location-btn">
            <LocateFixed className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Destination */}
        <div className="sf-search-row" onClick={() => setSelectingMode("destination")}>
          <div className="sf-dot-dest" />
          <div className="sf-search-ac" data-testid="destination-input" style={{ position: "relative" }}>
            <input type="text" className="sf-search-input" placeholder="To: type or click map" value={destinationInput}
              onChange={(e) => handleDestSearch(e.target.value)}
              onFocus={() => { setSelectingMode("destination"); if (destSuggestions.length > 0) setShowDestSuggestions(true); }}
              onKeyDown={(e) => { if (e.key === "Enter") setShowDestSuggestions(false); }}
              data-testid="destination-text-input"
            />
            {showDestSuggestions && destSuggestions.length > 0 && (
              <div className="sf-suggestions" data-testid="destination-suggestions">
                {destSuggestions.map((s, i) => (
                  <SuggestionItem key={s.place_id || s.display_name || i} s={s} onClick={selectDestSuggestion} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Waypoints */}
        {waypoints.length > 0 && (
          <div className="sf-waypoints-list">
            {waypoints.map((wp, i) => (
              <div key={`wp-${i}-${wp}`} className="sf-waypoint-tag">
                <MapPinned className="w-3 h-3" /><span>{wp}</span>
                <button onClick={() => removeWaypoint(i)} className="sf-waypoint-remove"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
        {showWaypointInput ? (
          <div className="sf-search-row">
            <div className="sf-dot-wp" />
            <input ref={waypointInputRef} type="text" value={waypointInput}
              onChange={(e) => setWaypointInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && waypointInput.trim()) addWaypoint(waypointInput.trim()); }}
              placeholder="Add a stop (city name)..." className="sf-search-input" data-testid="waypoint-input" autoFocus
            />
            <button onClick={() => setShowWaypointInput(false)} className="sf-waypoint-cancel"><X className="w-3.5 h-3.5" /></button>
          </div>
        ) : (
          <button onClick={() => setShowWaypointInput(true)} className="sf-add-stop-btn" disabled={waypoints.length >= 8} data-testid="add-waypoint-btn">
            <Plus className="w-3.5 h-3.5" />Add Stop
          </button>
        )}
        <div className="sf-search-buttons">
          <button onClick={calculateRoute} disabled={loadingRoute || !originInput || !destinationInput} className="sf-route-btn" data-testid="get-route-btn">
            <Navigation className="w-3.5 h-3.5" />{loadingRoute ? "CALCULATING..." : "GET ROUTE"}
          </button>
          {(originInput || destinationInput) && (
            <button onClick={clearRoute} className="sf-clear-btn" data-testid="clear-route-btn">Clear</button>
          )}
        </div>
      </div>

      {/* POI Toggle Bar */}
      <div className="absolute bottom-3 left-3 right-3 z-[999] flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()} data-testid="poi-toggle-bar">
        <button
          onClick={() => setPoiEnabled(!poiEnabled)}
          className={`flex items-center gap-1 text-[10px] font-['Manrope'] font-bold px-2 py-1 rounded-full border transition-all shadow-sm ${poiEnabled ? 'bg-white text-[#E52020] border-[#E52020]' : 'bg-white/80 text-[#999999] border-[#EBE6DC]'}`}
          data-testid="poi-master-toggle"
        >
          <MapPinned className="w-3 h-3" />POI
        </button>
        {poiEnabled && POI_OPTIONS.map(({ key, label, Icon, color }) => (
          <button
            key={key}
            onClick={() => togglePoiType(key)}
            className={`flex items-center gap-1 text-[10px] font-['Manrope'] px-2 py-1 rounded-full border transition-all shadow-sm ${poiTypes.includes(key) ? 'bg-white border-current' : 'bg-white/60 text-[#D4CEC0] border-[#EBE6DC]'}`}
            style={poiTypes.includes(key) ? { color, borderColor: color } : {}}
            data-testid={`poi-toggle-${key}`}
          >
            <Icon className="w-3 h-3" />{label}
          </button>
        ))}
        {poiEnabled && (
          addingCustomPin ? (
            <button onClick={onCancelCustomPin}
              className="flex items-center gap-1 text-[10px] font-['Manrope'] font-bold px-2 py-1 rounded-full border border-[#DC2626] text-[#DC2626] bg-white shadow-sm animate-pulse"
              data-testid="poi-cancel-pin">
              <X className="w-3 h-3" />Cancel
            </button>
          ) : (
            <button onClick={onStartCustomPin}
              className="flex items-center gap-1 text-[10px] font-['Manrope'] font-bold px-2 py-1 rounded-full border border-[#E85D04] text-[#E85D04] bg-white shadow-sm hover:bg-[rgba(232,93,4,0.06)]"
              data-testid="poi-add-pin">
              <Plus className="w-3 h-3" />Pin
            </button>
          )
        )}
      </div>

      {/* Route Info Badge — hidden in fullscreen */}
      {!mapFullscreen && routeInfo && (
        <>
        <div className="sf-route-badge">
          <div className="sf-route-stat"><Navigation className="w-4 h-4 text-[#16A34A]" /><span className="font-mono font-bold text-lg text-[#1A1A1A]">{routeInfo.distance} km</span></div>
          <div className="sf-route-stat"><Clock className="w-4 h-4 text-[#E52020]" /><span className="font-mono text-[#1A1A1A]">{routeInfo.duration}</span></div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => {
              if (!user) { toast.error("Sign in to save commutes"); setShowLogin(true); return; }
              setCommuteFromRoute({
                origin: routeInfo.originAddress || originInput, destination: routeInfo.destinationAddress || destinationInput,
                distance_km: routeInfo.distance, waypoints: waypoints.length > 0 ? waypoints : null,
                consumption, fuel_type: selectedFuelType, return_trip: returnTrip,
              });
            }} className="sf-save-commute-btn" data-testid="save-route-as-commute-btn">
              <Car className="w-3.5 h-3.5" />Save as Commute
            </button>
            {originCoords && destinationCoords && (
              <>
                <a href={buildGoogleMapsUrl(originCoords, destinationCoords, waypoints)} target="_blank" rel="noopener"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-['Manrope'] font-bold text-[#4285F4] bg-white border border-[#4285F4]/30 rounded-sm hover:bg-[#4285F4]/5 transition-colors"
                  data-testid="open-google-maps">
                  <Map className="w-3 h-3" />Google<ExternalLink className="w-2.5 h-2.5" />
                </a>
                <a href={buildAppleMapsUrl(originCoords, destinationCoords, originInput, destinationInput)} target="_blank" rel="noopener"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-['Manrope'] font-bold text-[#1A1A1A] bg-white border border-[#1A1A1A]/20 rounded-sm hover:bg-[#1A1A1A]/5 transition-colors"
                  data-testid="open-apple-maps">
                  <Map className="w-3 h-3" />Apple<ExternalLink className="w-2.5 h-2.5" />
                </a>
              </>
            )}
          </div>
        </div>
        </>
      )}
      {altRouteInfo && routeInfo && (
        <div className="sf-alt-route-badge" data-testid="alt-route-badge">
          <button onClick={() => switchRoute(altRouteInfo.index)} className="sf-alt-route-btn" data-testid="switch-alt-route-btn">
            <RotateCcw className="w-3.5 h-3.5" />
            <span>ALT ROUTE: {altRouteInfo.distanceKm} km &bull; {altRouteInfo.duration}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Route Stations Panel (best prices along route)
export function RouteStationsPanel({ routeStations, loadingStations, routeInfo, selectedFuelType, fuelStations }) {
  const [showStations, setShowStations] = useState(false);
  if ((!routeStations && !loadingStations) || !routeInfo) return null;
  const stations = fuelStations?.stations || [];
  return (
    <div className="sf-stations-panel" data-testid="route-stations-panel">
      <div className="sf-stations-header">
        <Fuel className="w-4 h-4 text-[#16A34A]" />
        <span>BEST PRICES ALONG YOUR ROUTE</span>
      </div>
      {loadingStations ? (
        <div className="flex items-center gap-2 py-2">
          <RefreshCw className="w-4 h-4 animate-spin text-[#E52020]" />
          <span className="text-sm text-[#666666] font-mono">Scanning stations...</span>
        </div>
      ) : routeStations && (
        <>
          <div className="sf-regions-path" data-testid="route-regions">
            {routeStations.regions_on_route.map((r, i) => (
              <span key={`region-${r}-${i}`} className="sf-region-tag">
                {i > 0 && <span className="sf-region-arrow">&rarr;</span>}
                {r}
              </span>
            ))}
          </div>
          {routeStations.regional_prices.length > 0 && (
            <div className="sf-regional-prices" data-testid="route-regional-prices">
              <p className="sf-section-label">Regional {FUEL_TYPES[selectedFuelType].label} Prices</p>
              {routeStations.regional_prices.map((rp, i) => (
                <div key={`rp-${rp.region_key || rp.region_name}-${i}`} className={`sf-price-row ${i === 0 ? 'cheapest' : ''}`}>
                  <span className="text-[#1A1A1A]">{rp.region_name}</span>
                  <span className="font-mono font-bold text-[#1A1A1A] flex items-center gap-2">
                    {formatCurrency(rp.price)}/L
                    {i === 0 && <span className="sf-cheapest-tag">CHEAPEST</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Fuel Stations List */}
          {stations.length > 0 && (
            <div className="mt-3 border-t border-[#EBE6DC] pt-3" data-testid="fuel-stations-list">
              <button onClick={() => setShowStations(!showStations)}
                className="w-full flex items-center justify-between text-xs font-['Manrope'] font-bold text-[#666666] tracking-wider">
                <span className="flex items-center gap-2">
                  <Fuel className="w-3.5 h-3.5 text-[#F59E0B]" />
                  {stations.length} FUEL STATIONS ALONG ROUTE
                </span>
                <span className="text-[#999999]">{showStations ? "\u25B2" : "\u25BC"}</span>
              </button>
              {showStations && (
                <div className="mt-2 space-y-1.5 max-h-[300px] overflow-y-auto" data-testid="fuel-stations-items">
                  {stations.map((s, i) => (
                    <div key={`${s.lat}-${s.lon}-${i}`} className={`flex items-center justify-between py-2 px-2.5 rounded-sm ${i === 0 ? 'bg-[rgba(22,163,74,0.06)] border border-[rgba(22,163,74,0.15)]' : 'bg-[#FAFAF7]'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-[#1A1A1A] truncate">{s.name}</div>
                        <div className="text-[10px] text-[#666666] flex items-center gap-2">
                          {s.brand && <span>{s.brand}</span>}
                          <span>{s.region}</span>
                          <span>{s.distance_from_route_km}km from route</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-mono font-bold text-sm text-[#1A1A1A]">{formatCurrency(s.estimated_price)}/L</span>
                        {i === 0 && <span className="text-[8px] font-bold text-[#16A34A] bg-[rgba(22,163,74,0.1)] px-1.5 py-0.5 rounded-sm">CHEAPEST</span>}
                      </div>
                    </div>
                  ))}
                  <p className="text-[9px] text-[#999999] text-center mt-2 italic">Prices are regional estimates based on Gaspy averages</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
